import type { BreadcrumbType, Event } from '@bugsnag/js'

// ignores dynamic import errors and elements errors that we can't do anything about
export const ignorePatterns = [
  /Failed to fetch dynamically imported module/,
  /JSON array expected\./,
  /cannot be parsed as a URL\./,
  /is not a valid URL\./,
]

export const shouldIgnoreBugsnagError = (errorMessage: string | undefined): boolean =>
  ignorePatterns.some(re => re.test(errorMessage ?? ''))

// Console output can contain user data and is rarely useful for triage, so leave log breadcrumbs out
export const enabledBreadcrumbTypes: BreadcrumbType[] = ['error', 'manual', 'navigation', 'process', 'request', 'state', 'user']

// Errors thrown entirely by scripts we don't serve (chat widget, browser extensions, injected scripts)
// are not actionable. Our bundles are same-origin, so any frame from another origin is third party.
export const isThirdPartyError = (event: Event, ownHost: string = globalThis.location?.host): boolean => {
  const files = (event.errors?.[0]?.stacktrace ?? [])
    .map(frame => frame.file)
    .filter((file): file is string => Boolean(file))

  // No usable frames (e.g. cross-origin "Script error."): can't tell, keep the event
  if (files.length === 0 || !ownHost) { return false }

  return !files.some((file) => {
    try {
      return new URL(file, globalThis.location?.href).host === ownHost
    } catch {
      return false
    }
  })
}

// Promises rejected with a non-Error (string, plain object, Response) have no usable stack to act on
export const isNonErrorRejection = (event: Event): boolean =>
  event.unhandled === true && event.originalError !== undefined && !(event.originalError instanceof Error)

export const shouldDropBugsnagEvent = (event: Event): boolean =>
  shouldIgnoreBugsnagError(event.errors?.[0]?.errorMessage) || isThirdPartyError(event) || isNonErrorRejection(event)
